import React, { useMemo, useState } from 'react'
import { useStateContext } from '../contexts/ContextProvider'
import { getThemeNames, getThemeColors, isColorDark } from '../config/themes'

// Human-readable label from a theme's raw config name, e.g.
// "darkandstormynight" -> "Darkandstormynight", "TemplarEdgeDark" ->
// "Templar Edge Dark". Approximate for the all-lowercase custom theme
// names (no word boundaries to split on), which is fine for a picker list.
function labelFor(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

interface ThemeEntry {
  name: string
  label: string
  dark: boolean
  swatches: [string, string, string, string]
}

const ALL_THEMES: ThemeEntry[] = getThemeNames().map((name) => {
  const colors = getThemeColors(name)
  return {
    name,
    label: labelFor(name),
    dark: isColorDark(colors.base),
    swatches: [colors.primary, colors.secondary, colors.accent, colors.base],
  }
})

const ThemeSettings: React.FC = () => {
  const { currentTheme, setCurrentTheme, themeSettings, setThemeSettings } = useStateContext()
  const [searchTerm, setSearchTerm] = useState('')

  const { darkThemes, lightThemes } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const filtered = term ? ALL_THEMES.filter(t => t.label.toLowerCase().includes(term)) : ALL_THEMES
    return {
      darkThemes: filtered.filter(t => t.dark),
      lightThemes: filtered.filter(t => !t.dark),
    }
  }, [searchTerm])

  if (!themeSettings) return null

  const renderTheme = (t: ThemeEntry) => (
    <button
      key={t.name}
      onClick={() => setCurrentTheme(t.name)}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left w-full
        ${currentTheme === t.name
          ? 'border-primary bg-primary/10 ring-2 ring-primary'
          : 'border-base-300 hover:bg-base-200'}`}
    >
      <div className="flex gap-1 shrink-0">
        {t.swatches.map((hex, i) => (
          <span
            key={i}
            className="w-4 h-4 rounded-full border border-black/10"
            style={{ backgroundColor: hex }}
          />
        ))}
      </div>
      <span className="text-sm font-medium flex-1 whitespace-nowrap text-left">{t.label}</span>
      {currentTheme === t.name && <span className="badge badge-xs badge-primary shrink-0">Active</span>}
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={() => setThemeSettings(false)} />
      <div className="relative bg-base-100 w-[28rem] h-full shadow-2xl flex flex-col border-l border-base-300">
        <div className="flex items-center justify-between p-4 border-b border-base-300 shrink-0">
          <h2 className="text-lg font-bold">Theme Settings</h2>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={() => setThemeSettings(false)}>✕</button>
        </div>
        <div className="p-4 border-b border-base-300 shrink-0">
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            placeholder="Search themes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {darkThemes.length > 0 && (
            <>
              <p className="px-1 text-xs font-semibold uppercase text-base-content/50 tracking-widest">Dark Themes</p>
              {darkThemes.map(renderTheme)}
            </>
          )}
          {lightThemes.length > 0 && (
            <>
              <p className="px-1 pt-2 text-xs font-semibold uppercase text-base-content/50 tracking-widest">Light Themes</p>
              {lightThemes.map(renderTheme)}
            </>
          )}
          {darkThemes.length === 0 && lightThemes.length === 0 && (
            <div className="p-8 text-center text-base-content/50 text-sm">No themes found matching "{searchTerm}"</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ThemeSettings
